import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type IdentifiedCriticalOperation, IdentifiedCriticalOperationForm } from './IdentifiedCriticalOperationForm';

export const Route = createFileRoute(
    '/console/$orgId/configuration/compliance/mspr/critical-operations/identified/edit/$id',
)({
    component: EditCriticalOperationRoute,
    params: {
        parse: ({ id }) => ({ id: BigInt(id) }),
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditCriticalOperationRoute() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { id } = Route.useParams();

    const { data: criticalOperation, isPending: isLoading } = trpc.mspr.criticalOperation.getCriticalOperation.useQuery(
        {
            id,
        },
        { refetchOnWindowFocus: false },
    );

    const { mutate: updateIdentifiedCriticalOperation, isPending: isUpdating } =
        trpc.mspr.criticalOperation.updateCriticalOperation.useMutation();

    const handleSubmit = (values: IdentifiedCriticalOperation) => {
        message.loading('Updating identified critical operation...');
        updateIdentifiedCriticalOperation(
            {
                id,
                set: {
                    subType: values.subType ?? null,
                    maxTimeToleranceInHours: values.maxTimeToleranceInHours ?? null,
                    dataLossTolerance: values.dataLossTolerance ?? null,
                    serviceLevelTolerance: values.serviceLevelTolerance ?? null,
                },
            },
            {
                onSuccess: () => {
                    message.success('Identified critical operation updated');
                    trpcUtils.mspr.invalidate();
                    setOpen(false);
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <IdentifiedCriticalOperationForm
                open={open}
                mode="edit"
                data={criticalOperation}
                state={match({ isLoading, isUpdating })
                    .with({ isLoading: true }, () => 'loading' as const)
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/compliance/mspr/critical-operations',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
